/**
 * The shapes the document list reads off the wire, and the handful of pure
 * functions the list, the rail, the toolbar and the dialogs share.
 *
 * They live beside the components that use them rather than in `lib/types.ts`
 * because the list, the rail and the dialogs are one feature and share exactly
 * these shapes.
 *
 * `role` comes from the SERVER, on every row, computed by the same expression
 * `assertDocAccess` enforces. The browser never infers what a person may do
 * from the shape of the data it happens to have: a menu assembled from a guess
 * is a menu that offers actions the API will refuse, or hides ones it would
 * have allowed.
 */

export type Role = 'viewer' | 'commenter' | 'editor' | 'owner'

/**
 * The root, in one place because it is what a PERSON reads.
 *
 * TWO strings, and the split is deliberate. The user asked for `/` on screen;
 * a design review objected that `/` reads as punctuation and is unreadable
 * when announced. Both are right, and they are about two different things:
 *
 *   ROOT_GLYPH  what the screen DRAWS wherever an element NAMES the place --
 *               the breadcrumb crumb, the tree row, the pane heading, the move
 *               dialog's first radio. Every one of those carries
 *               `aria-label={ROOT_NAME}`, so the screen is `/` and the
 *               announcement is still a word.
 *   ROOT_NAME   the word used in SENTENCES -- notices, dialog titles, confirm
 *               copy -- where "moved to /" would be a sentence with a
 *               punctuation mark where its noun should be.
 *
 * Neither is on the wire, and nothing about the wire changed:
 * `GET /v1/folders/root/children` still answers `path: []` and
 * `GET /v1/docs?folder=unfiled` still keeps its word, because that word names
 * a predicate -- documents with no placement row -- while these name a place
 * (TASK-042 §1). The reserved-name list is untouched for the same reason.
 */
export const ROOT_NAME = 'Root'
export const ROOT_GLYPH = '/'

/**
 * Folder sharing is TASK-040, sixteen days of work, unbuilt.
 *
 * The slot and its position are decided now so that turning it on disturbs
 * nothing: Share sits to the LEFT of Rename, outside the set of three whose
 * order the requirement fixed, which leaves Delete furthest right (§3a, §3d).
 * TASK-040 flips this to true and writes the dialog behind it. A control that
 * is present and disabled is a promise rendered on every visit.
 */
export const FOLDER_SHARING = false

export interface Folder {
  id: string
  name: string
  /** `null` is the root. The root is a view, not a folder. */
  parentId: string | null
  /** Ancestor count: 0 at the root, 10 at the deepest the server allows. */
  depth: number
  /** This person's placements in it, archived documents excluded. */
  docCount: number
  /**
   * An optimistic row the server has not confirmed yet (TASK-042 §2 of the
   * performance work). It is drawn, because the whole point is that the screen
   * does not wait -- but it is not a navigation target, because its id is a
   * local placeholder and following it would ask the server for a folder that
   * does not exist yet.
   */
  pending?: boolean
}

/** The same folder, with its children hung off it. Built in the browser. */
export interface FolderNode extends Folder {
  children: FolderNode[]
}

export interface DocRow {
  id: string
  name: string
  rev: number
  updated_at: string
  /** The AUTHOR's display name -- who wrote it, not whose it is. */
  created_by?: string | null
  /**
   * Where I have filed it. `null` is the ROOT -- which is the absence of a
   * placement row, not a row pointing at nothing (TASK-039 §6).
   */
  folderId: string | null
  role: Role
  /** Set only in the archived view. */
  archivedAt?: string | null
}

/**
 * How the pane draws what is in it. Per PERSON, not per folder (§5a).
 *
 * Finder and Explorer key this by folder because folders differ in kind -- a
 * folder of photographs wants icons and a folder of invoices wants a list.
 * That argument does not apply here: this corpus is entirely prose documents,
 * so every folder is the same kind of folder, and a tile is a structural
 * sketch rather than a photograph. Per folder would need a table, a row per
 * folder per person, an answer for the root (which has no id), an answer for a
 * folder shared with you, and a cleanup on folder delete.
 *
 * "Per person" has to mean KEYED BY PERSON -- `unomena.docs.view:<user id>`,
 * through `lib/me.ts` -- and not one unkeyed entry, which is per browser
 * PROFILE and gives a different answer the moment two people share one.
 */
export type PaneView = 'list' | 'icons'

/**
 * The tile preview: a STRUCTURAL sketch, never a copy of the text.
 *
 * Paragraph lengths arrive bucketed to three widths, deliberately, so the
 * sketch cannot be read back as a low-resolution copy of the document
 * (TASK-042 §5b). `heading` is the exception and is the stated trade: it is up
 * to 80 characters of the document's own words, which is the same disclosure
 * the document's NAME already makes to the same audience.
 */
export type SketchMark =
  | { k: 'h'; level: 1 | 2 | 3 | 4 | 5 | 6 }
  | { k: 'p'; lines: 1 | 2 | 3 }
  | { k: 'list'; items: 1 | 2 | 3 }
  | { k: 'table'; cols: number; rows: number }
  | { k: 'code' }
  | { k: 'quote' }
  | { k: 'figure' }
  | { k: 'pending' }

export interface Sketch {
  heading: string | null
  marks: SketchMark[]
  truncated: boolean
}

/** One entry in the breadcrumb. `root` is the flag, never a name comparison. */
export interface Crumb {
  id: string | null
  name: string
  root?: boolean
}

/**
 * Which place, or which built-in view, the screen is showing.
 *
 * `root` replaces phase 1's `unfiled`. Nothing about the data changed: the
 * root is still `parent_id IS NULL` for folders and the absence of a placement
 * row for documents. What changed is that the browser stops naming the place
 * after the query that produces it.
 */
export type View =
  | { kind: 'all' }
  | { kind: 'root' }
  | { kind: 'folder'; id: string }
  | { kind: 'archived' }

/**
 * The view a URL addresses, and the URL a view is addressed by.
 *
 * A folder that cannot be linked, bookmarked or reached with Back is a
 * filtered list rather than a browser, which is the whole of §1a. `/` is the
 * root with no parameter at all, because the root is where a file browser
 * opens and a bare path is the honest spelling of it.
 *
 *   /                  the root
 *   /?folder=<uuid>    that folder
 *   /?folder=all       All documents
 *   /?folder=trash     Trash (still labelled "Deleted" until §8 ships)
 *
 * `unfiled` and `root` are accepted in the parameter and both mean the root:
 * the first is the word the wire uses for the predicate and somebody will type
 * it, and answering it with a 400 would be a pedantic way to break a link.
 */
export function viewFromQuery(params: URLSearchParams): View {
  const folder = params.get('folder')
  if (!folder || folder === 'root' || folder === 'unfiled') return { kind: 'root' }
  if (folder === 'all') return { kind: 'all' }
  if (folder === 'trash') return { kind: 'archived' }
  return { kind: 'folder', id: folder }
}

export function viewHref(view: View): string {
  if (view.kind === 'all') return '/?folder=all'
  if (view.kind === 'archived') return '/?folder=trash'
  if (view.kind === 'folder') return `/?folder=${encodeURIComponent(view.id)}`
  return '/'
}

/**
 * The query string `GET /v1/docs` wants for a view.
 *
 * Only `all` and `archived` reach it now: a folder and the root both go
 * through `GET /v1/folders/:id/children`, which answers "what is in this
 * place". The root's spelling is kept here anyway, and it keeps the wire's
 * word, because the predicate did not change its name when the place got one.
 */
export function viewQuery(view: View): string {
  if (view.kind === 'archived') return '?archived=1'
  if (view.kind === 'root') return '?folder=unfiled'
  if (view.kind === 'folder') return `?folder=${encodeURIComponent(view.id)}`
  return ''
}

/**
 * What a document row may offer, computed once for both presentations.
 *
 * List view renders it as buttons (`DocRowActions`) and icon view renders it
 * as a dropdown (`DocRowMenu`). One function, so the two cannot drift into
 * offering different things -- which is what happens when the same four rules
 * are written twice in two components (§3b).
 *
 * The rules, and the two that MOVED in TASK-038 phase 2:
 *
 *  * Archived is Restore and nothing else.
 *  * **Share… is the owner's, and it sits FIRST**, to the left of Rename, which
 *    leaves Delete furthest right -- the part of TASK-042 §3a's order that has
 *    a safety reason behind it.
 *  * **Rename is an EDITOR's right now, not the owner's alone.** The user
 *    settled it: *"if you have editing rights, you may edit, including rename a
 *    document"* (TASK-038, Product decisions). The server moved with it, in
 *    both places -- `PATCH /v1/docs/:id` and the MCP `update` tool -- and this
 *    is the third place, because a button hidden from somebody the API would
 *    have allowed is the same disagreement seen from the other side.
 *  * Move to... always, because filing needs only `read` (TASK-038 §5).
 *  * **Remove from my list** for everybody who is NOT the owner. The owner is
 *    never offered it (§3): their choices are hand it over or delete it for
 *    everyone, and the server refuses them by name if they try.
 *
 * Nothing here is inferred -- `doc.role` comes from the server on every row,
 * computed by the same expression `assertDocAccess` enforces.
 */
export type DocActionKind = 'share' | 'rename' | 'move' | 'delete' | 'leave' | 'restore'

export function docActions(doc: DocRow, opts: { archived: boolean }): DocActionKind[] {
  if (opts.archived) return ['restore']
  const owner = doc.role === 'owner'
  const out: DocActionKind[] = []
  if (owner) out.push('share')
  if (owner || doc.role === 'editor') out.push('rename')
  out.push('move')
  if (owner) out.push('delete')
  else out.push('leave')
  return out
}

/* ------------------------------------------------------------- sharing */

/** The three roles a share may grant. `owner` is reached by transfer alone. */
export const SHAREABLE_ROLES: Role[] = ['viewer', 'commenter', 'editor']

/**
 * What a role lets somebody do, in the words the dialog puts beside it.
 *
 * `commenter` is on the list because the SYSTEM has four roles: the `acls.role`
 * CHECK, `ROLE_RANK` and the whole comment surface all know it. The ticket's
 * Product decisions section names three and says in as many words not to drop
 * the fourth on the strength of that sentence.
 */
export const ROLE_BLURB: Record<string, string> = {
  viewer: 'Can read it',
  commenter: 'Can read it and comment',
  editor: 'Can read, comment and edit',
  owner: 'Owns it',
}

/** How somebody came to hold what they hold. From the server, never inferred. */
export type AccessVia = 'owner' | 'direct' | 'folder' | 'org' | 'link'

export interface AccessEntry {
  /** Null for the org-wide and link grants, which are not a person. */
  userId: string | null
  name: string
  email: string | null
  role: Role
  /** Whether this grant may be passed on. TASK-040 is what USES it. */
  mayShare: boolean
  via: AccessVia
  /**
   * True when this person has never signed in -- the account was created by the
   * share itself (TASK-038 phase 4). The row is inert until they do: no
   * session, no token, nothing that treats a `users` row as authorisation.
   * `name` is their address in the meantime, never null.
   */
  pending: boolean
  grantedBy: { userId: string | null; name: string } | null
  /**
   * Live tokens this person holds.
   *
   * On the screen because it is the largest consequence of sharing and the one
   * a person cannot otherwise see: a share reaches the recipient's account AND
   * every agent they have connected, at the same role, immediately (§8).
   */
  liveTokens: number
  createdAt: string | null
  expiresAt: string | null
}

export interface AccessList {
  docId: string
  name: string
  entries: AccessEntry[]
  /**
   * Published to the web (TASK-049), and when. Carried on the access list
   * rather than fetched separately because "everyone" is an answer to the
   * question the dialog asks, and two fetches could show two vintages of it.
   */
  public?: boolean
  publicAt?: string | null
  /** May this caller publish it? The server's answer, not the browser's. */
  canPublish?: boolean
}

/**
 * One address the dialog was given, and what became of it.
 *
 * The share endpoint takes ONE recipient per call, so several addresses are
 * several requests -- and this is what makes the progress honest: the number a
 * person watches tick up is the number of grants that actually happened, not a
 * server-side batch reporting "three of five".
 */
export interface ShareAttempt {
  email: string
  state: 'waiting' | 'sending' | 'done' | 'failed'
  /** The server's own sentence on a failure; never a JSON blob. */
  error?: string
  /** False when the grant landed but the mail did not. Not a failure. */
  mailed?: boolean
  /**
   * The share created the account (TASK-038 phase 4). A different outcome from
   * sharing with a colleague, and the confirmation says so -- the document is
   * waiting for a first sign-in rather than being read.
   */
  pending?: boolean
}

/**
 * Split what somebody typed into addresses.
 *
 * Commas, semicolons, spaces and newlines, because a person pasting from a
 * mail client gets whichever of those that client used, and a dialog that
 * accepts one of them is a dialog that silently drops four people.
 *
 * Duplicates are collapsed CASE-INSENSITIVELY -- the server normalises
 * addresses, so `Ana@x.com` and `ana@x.com` are one person, and issuing two
 * requests would show a person two lines of progress for one grant.
 */
export function splitAddresses(raw: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of raw.split(/[\s,;]+/)) {
    const trimmed = part.trim()
    if (!trimmed) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(trimmed)
  }
  return out
}

/** The shape of an address, loosely: the server is what actually decides. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(value)
}

/* ------------------------------------------------- the address FIELD */

/**
 * The keys that turn what has been typed into a pill.
 *
 * The user asked for it in these words: *"when I press space, comma, semicolon
 * or whatever after the email address, it needs to validate a valid email
 * address, and then that needs to become a pill"*. Enter and Tab are here for
 * the same reason every other token field has them -- Enter is what a person
 * presses at the end of a line, and Tab is what they press when they have
 * finished with the field.
 *
 * TAB IS THE ODD ONE and the caller must treat it as such: it commits, but it
 * must NOT be swallowed, or the field becomes a keyboard trap. The caller
 * preventDefaults the other four and lets Tab through.
 */
export function commitsAddress(key: string): boolean {
  return key === ' ' || key === ',' || key === ';' || key === 'Enter' || key === 'Tab'
}

/** What one commit did: enough to redraw the field AND to say it out loud. */
export interface AddressCommit {
  /** The pills afterwards, in order. */
  tokens: string[]
  /** What became a pill just now. */
  added: string[]
  /** Already pilled, case-insensitively. A no-op, NOT an error (§dedupe). */
  duplicates: string[]
  /** What is left IN THE INPUT: everything that did not pass. */
  rest: string
  /** One short line, or '' when everything landed. */
  error: string
}

/**
 * Commit typed or pasted text against the pills that are already there.
 *
 * Three rules, and each of them is a way token fields usually go wrong:
 *
 *  * An address that does not pass `looksLikeEmail` does NOT vanish and does
 *    NOT become a pill. It stays in `rest` -- which the caller puts straight
 *    back in the input -- and `error` says why. A field that eats a typo is a
 *    field that shares a document with nobody and says nothing.
 *  * A PASTE of several addresses becomes several pills in one go, because
 *    that is the common case for this dialog and typing a delimiter after each
 *    of nine addresses is not a thing anybody should be asked to do.
 *  * A duplicate is a no-op, compared case-insensitively, because that is what
 *    the SERVER does with the address: `Ana@x.com` and `ana@x.com` are one
 *    person and two pills would be two requests and two lines of progress for
 *    one grant.
 *
 * The validator is `looksLikeEmail` and there is no second one. It is looser
 * than the server's `isEmail` in one direction only (it does not cap the
 * length, and it accepts a one-letter TLD), which is the safe direction: a
 * field stricter than the server refuses addresses the system would accept.
 */
export function commitAddresses(tokens: string[], raw: string): AddressCommit {
  const out = [...tokens]
  const have = new Set(tokens.map((t) => t.toLowerCase()))
  const added: string[] = []
  const duplicates: string[] = []
  const bad: string[] = []

  for (const candidate of splitAddresses(raw)) {
    if (!looksLikeEmail(candidate)) {
      bad.push(candidate)
      continue
    }
    const key = candidate.toLowerCase()
    if (have.has(key)) {
      duplicates.push(candidate)
      continue
    }
    have.add(key)
    out.push(candidate)
    added.push(candidate)
  }

  return {
    tokens: out,
    added,
    duplicates,
    rest: bad.join(', '),
    error: bad.length ? addressProblem(bad) : '',
  }
}

/** The one short line under the field. Names the address, so it can be fixed. */
export function addressProblem(bad: string[]): string {
  if (bad.length === 1) return `“${bad[0]}” does not look like an email address.`
  return `These do not look like email addresses: ${bad.join(', ')}`
}

/**
 * What the live region says about a commit.
 *
 * Additions and removals are invisible to somebody who cannot see the pills
 * appear, and "already on the list" is the difference between a no-op and a
 * field that ignored them.
 */
export function addressNews(commit: AddressCommit): string {
  const said: string[] = []
  if (commit.added.length === 1) said.push(`Added ${commit.added[0]}`)
  else if (commit.added.length > 1) said.push(`Added ${commit.added.length} addresses`)
  if (commit.duplicates.length === 1) {
    said.push(`${commit.duplicates[0]} is already on the list`)
  } else if (commit.duplicates.length > 1) {
    said.push(`${commit.duplicates.length} addresses were already on the list`)
  }
  if (commit.error) said.push(commit.error)
  return said.join('. ')
}

/** Where focus goes when the pill at `index` is removed from `count` pills. */
export type RemoveFocus = { target: 'pill'; index: number } | { target: 'input' }

/**
 * Focus after a removal, decided rather than left to chance.
 *
 * The button that was focused has just been deleted, so focus falls to
 * `<body>` unless something puts it somewhere -- and a keyboard user whose
 * focus is on `<body>` inside a modal is stranded. The next pill's remove
 * button takes it, which keeps a run of removals going; the input takes it
 * when there is no next pill, which is where a person carries on typing.
 */
export function focusAfterRemove(count: number, index: number): RemoveFocus {
  return index < count - 1 ? { target: 'pill', index } : { target: 'input' }
}

/**
 * Backspace on an EMPTY input removes the last pill: which one, or none.
 *
 * Only when the input is empty. With a caret in text, Backspace deletes a
 * character and nothing else, because a field that ate a pill while somebody
 * was correcting a typo would be unusable.
 */
export function backspaceRemoves(value: string, count: number): number | null {
  if (value !== '' || count === 0) return null
  return count - 1
}

/**
 * The flat `GET /v1/folders` response, as a tree.
 *
 * Built here rather than served that way: a person's tree is tens of rows, so
 * one flat array is one request and one cache entry, and the server stays out
 * of the business of shaping a rail (TASK-039 §9).
 *
 * A folder whose parent is missing from the list is hung at the ROOT rather
 * than dropped. The server cannot produce one -- but a folder that vanishes
 * from the rail while its documents stay in it is the one failure this screen
 * must not have.
 *
 * The Root node is NOT synthesised in here. It is rendered above this
 * function's output by the rail, because it has no row and never gains one.
 */
export function buildFolderTree(folders: Folder[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>()
  for (const f of folders) nodes.set(f.id, { ...f, children: [] })
  const roots: FolderNode[] = []
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  const sort = (list: FolderNode[]) => {
    list.sort(byName)
    for (const n of list) sort(n.children)
  }
  sort(roots)
  return roots
}

export function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}

/** The direct children of a place, for the content pane. `null` is the root. */
export function childFolders(folders: Folder[], parentId: string | null): Folder[] {
  return folders.filter((f) => f.parentId === parentId).sort(byName)
}

/** Depth-first, so an indented flat list reads in tree order. */
export function flattenTree(nodes: FolderNode[], depth = 0): { folder: Folder; depth: number }[] {
  const out: { folder: Folder; depth: number }[] = []
  for (const node of nodes) {
    out.push({ folder: node, depth })
    out.push(...flattenTree(node.children, depth + 1))
  }
  return out
}

/** Root first, the folder itself last. Bounded, because the tree is. */
export function folderAncestry(folders: Folder[], id: string): Folder[] {
  const byId = new Map(folders.map((f) => [f.id, f]))
  const path: Folder[] = []
  let cursor = byId.get(id)
  // The bound is the server's depth limit plus one; a cycle cannot reach the
  // browser, and a loop that trusted the data to be acyclic would hang the tab.
  for (let i = 0; cursor && i <= 11; i++) {
    path.unshift(cursor)
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined
  }
  return path
}

/**
 * The breadcrumb above the list: what this view is, and what it is inside.
 *
 * Always rooted and always at least one entry, so it is always rendered. A
 * crumb whose `id` is `null` is the root itself -- the same `null` that every
 * other piece of folder state on this screen already means the root by.
 *
 * All documents and Trash get a single crumb naming themselves, with no Root
 * above them: they are not places, and they are not inside one.
 *
 * `root: true` marks the one crumb that is drawn as `ROOT_GLYPH` and announced
 * as `ROOT_NAME`. It is a flag rather than a comparison against the name,
 * because "All documents" also carries a `null` id and is not the root.
 */
export function viewPath(view: View, folders: Folder[]): Crumb[] {
  if (view.kind === 'archived') return [{ id: null, name: 'Trash' }]
  if (view.kind === 'all') return [{ id: null, name: 'All documents' }]
  const root: Crumb = { id: null, name: ROOT_NAME, root: true }
  if (view.kind === 'root') return [root]
  const path = folderAncestry(folders, view.id)
  // A folder id the rail has not loaded yet: name it neutrally rather than
  // dropping the crumb, so the breadcrumb does not flicker down to the root
  // and back while `GET /v1/folders` is in flight.
  return path.length
    ? [root, ...path.map((f) => ({ id: f.id as string | null, name: f.name }))]
    : [root, { id: null, name: 'Folder' }]
}

/** What the heading above the list says: the last thing in the path. */
export function viewTitle(view: View, folders: Folder[]): string {
  const path = viewPath(view, folders)
  return path[path.length - 1]?.name ?? ROOT_NAME
}

/* ------------------------------------------------- optimistic tree edits */

/**
 * The four folder mutations, as PURE functions over the flat list.
 *
 * They are here, beside the shapes, rather than in the store that calls them,
 * for one reason: the store needs a DOM and a fetch and cannot be unit-tested,
 * and these are the parts that decide whether the optimistic screen and the
 * server's answer agree. Each returns a NEW array; nothing is mutated, so the
 * caller's snapshot is a valid revert with no copying of its own.
 *
 * Every one of them mirrors what `service/folders.ts` does, and where the two
 * could drift the comment says which line of the service it is mirroring.
 */

/** A folder and everything under it. Bounded by the server's depth cap. */
export function subtreeIds(folders: Folder[], id: string): Set<string> {
  const byParent = new Map<string | null, Folder[]>()
  for (const f of folders) {
    const list = byParent.get(f.parentId) ?? []
    list.push(f)
    byParent.set(f.parentId, list)
  }
  const out = new Set<string>([id])
  const stack = [id]
  // The bound is the server's depth limit; a cycle cannot reach the browser,
  // and a walk that trusted the data to be acyclic would hang the tab.
  for (let guard = 0; stack.length && guard <= 4096; guard++) {
    const next = stack.pop() as string
    for (const child of byParent.get(next) ?? []) {
      if (out.has(child.id)) continue
      out.add(child.id)
      stack.push(child.id)
    }
  }
  return out
}

export function withFolderAdded(folders: Folder[], folder: Folder): Folder[] {
  return [...folders.filter((f) => f.id !== folder.id), folder]
}

export function withFolderRenamed(folders: Folder[], id: string, name: string): Folder[] {
  return folders.map((f) => (f.id === id ? { ...f, name } : f))
}

/**
 * Move a folder, carrying its subtree's depth with it.
 *
 * `depth` is what the local pre-check for "that would nest too deep" reads, so
 * leaving the subtree at its old depth would make the browser invite a move
 * the server then refuses.
 */
export function withFolderMoved(
  folders: Folder[],
  id: string,
  parentId: string | null,
): Folder[] {
  const moving = folders.find((f) => f.id === id)
  if (!moving) return folders
  const parentDepth = parentId ? (folders.find((f) => f.id === parentId)?.depth ?? 0) : -1
  const shift = parentDepth + 1 - moving.depth
  const subtree = subtreeIds(folders, id)
  return folders.map((f) => {
    if (f.id === id) return { ...f, parentId, depth: f.depth + shift }
    if (subtree.has(f.id)) return { ...f, depth: f.depth + shift }
    return f
  })
}

/**
 * Delete a folder the way the SERVER deletes one: its subfolders move up one
 * level into its parent, and its documents go to the root.
 *
 * The documents leave the tree entirely rather than landing on the parent's
 * count, because the root has no row to count them on -- which is the whole of
 * §1 and the reason the notice says "moved to Root" rather than naming the
 * parent.
 */
export function withFolderDeleted(folders: Folder[], id: string): Folder[] {
  const gone = folders.find((f) => f.id === id)
  if (!gone) return folders
  const children = new Set(folders.filter((f) => f.parentId === id).map((f) => f.id))
  const subtree = subtreeIds(folders, id)
  return folders
    .filter((f) => f.id !== id)
    .map((f) => {
      if (children.has(f.id)) return { ...f, parentId: gone.parentId, depth: f.depth - 1 }
      if (subtree.has(f.id)) return { ...f, depth: f.depth - 1 }
      return f
    })
}

/**
 * A document changing folders, as the two counts it changes.
 *
 * `null` on either side is the root, which has no row and therefore no count
 * to adjust -- so a move to or from the root touches exactly one folder.
 */
export function withDocMoved(
  folders: Folder[],
  from: string | null,
  to: string | null,
): Folder[] {
  if (from === to) return folders
  return folders.map((f) => {
    if (f.id === from) return { ...f, docCount: Math.max(0, f.docCount - 1) }
    if (f.id === to) return { ...f, docCount: f.docCount + 1 }
    return f
  })
}

export function sameView(a: View, b: View): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'folder' && b.kind === 'folder') return a.id === b.id
  return true
}
